// Shared pieces of the "learn from what people decide" framework (docs/implementation/LEARNING_SYSTEMS.md).
//
// The contract every learning domain follows:
//   1. CAPTURE — the engine writes down what it knew when it made a choice (inputs + what it chose), and a
//      person's accept / correct is stamped on the same row (db/86 ReviewStatus). No capture, nothing to learn.
//   2. AGGREGATE — a read proc groups ONLY human-decided rows (never the engine's own unreviewed output) into
//      counts per situation + choice, one row per project's latest revision so copies are not counted twice.
//   3. PROPOSE — pure functions turn the counts into proposals for an admin to approve (LearnedProposals);
//      nothing becomes a rule by itself.
//   4. PREDICT — the same counts pre-fill a blank choice, flagged unreviewed, never over a rule or a person's pick.
// Thresholds are Takeoff Variables (Category 'wall_learning' for the first domain), so tuning needs no deploy.

export const DEFAULT_LEARNING_TUNABLES = {
  LEARN_MIN_SAMPLES: 5,
  LEARN_MIN_PROJECTS: 2,
  LEARN_MIN_AGREEMENT: 0.8,
  LEARN_PREDICT_ENABLED: 1,
  LEARN_PREDICT_MIN_SAMPLES: 3,
  LEARN_PREDICT_MIN_PROJECTS: 1,
  LEARN_PREDICT_MIN_AGREEMENT: 0.75,
  LEARN_GAP_MARGIN_FT: 0.15,
};

// The learning domains the platform knows about. `live` = capture + proposals + (where it applies) prediction are
// built; `planned` = what to capture and what the learned output would be, so the data starts accumulating before
// the screen exists. Keep this list and the planning doc in step.
export const LEARNING_DOMAINS = [
  {
    id: "wall_construction", label: "Wall construction", status: "live",
    learns: "Which Item / Assembly a boundary between two room types gets (shared wall, cavity face, exterior).",
    signal: "Human-accepted or corrected auto walls (TakeoffConditionsDrawn AutoWall* facts + ReviewStatus).",
    output: "Construction Standard rules, template wall systems by position, and unreviewed pre-fills.",
  },
  {
    id: "room_elements", label: "Room template elements", status: "planned",
    learns: "Which Standard Elements (HEPA, lights, doors, finishes) rooms of a template actually end up with.",
    signal: "RoomElements per room joined to TakeoffRooms.RoomTemplateID, plus add / remove / quantity edits.",
    output: "Proposed Standard Elements per Room Template (templates are empty today).",
  },
  {
    id: "engine_overrides", label: "Engine override patterns", status: "planned",
    learns: "Where estimators override the HEPA count, lighting count, or ACH, and by how much, per room template.",
    signal: "Criterion override rows vs TakeoffRoomEngineering calculated values.",
    output: "Calibrated design defaults (design CFM per filter, fixture spacing) proposed per template.",
  },
  {
    id: "schedule_durations", label: "Schedule durations", status: "planned",
    learns: "How far estimator duration overrides sit from the production standards, per activity type.",
    signal: "ScheduleActivities override + reason code vs calculated duration (already stored).",
    output: "Proposed Production Standard versions (never applied without approval).",
  },
  {
    id: "quote_line_matches", label: "Quote line to catalog matches", status: "planned",
    learns: "Which catalog Item a vendor's quote line description maps to.",
    signal: "QuoteLineItems MatchStatus confirmed / rejected (already stored).",
    output: "Ranked suggested matches for new quote lines.",
  },
  {
    id: "estimate_adjustments", label: "Estimate price adjustments", status: "planned",
    learns: "Where estimators type over catalog unit cost, burden, markup or quantity, per Item.",
    signal: "EstimateRollupOverrides / carried quote prices vs the catalog price.",
    output: "Catalog price refresh proposals with the evidence (cost-band pattern, db/50).",
  },
  {
    id: "equipment_assumptions", label: "Assumed equipment", status: "planned",
    learns: "Typical equipment and connection loads per room type / system architecture.",
    signal: "ProcessEquipment + EquipmentConnections across projects, AssumptionStatus.",
    output: "Proposed Equipment Library defaults.",
  },
];

/** 'item:7' / 'assembly:3' for a group, a rule row or a drawn row (Kind + ItemID / AssemblyID in either casing). */
export function entityKeyOf(x) {
  const kind = x.kind ?? x.Kind;
  const id = kind === "item" ? (x.itemId ?? x.ItemID) : (x.assemblyId ?? x.AssemblyID);
  return kind && id != null ? `${kind}:${+id}` : null;
}

/** The canvas's own chooser key ('item:7' / 'asm:3', TakeoffCanvas ruleTypeKey) for a group. */
export function canvasTypeKey(g) {
  return g.kind === "item" ? `item:${g.itemId}` : `asm:${g.assemblyId}`;
}

/** Counts for a set of choice groups: the winner, the runner-up, the total and the winner's share. */
export function pickWinner(groups) {
  const sorted = [...groups].sort((a, b) => b.samples - a.samples);
  const total = sorted.reduce((s, g) => s + g.samples, 0);
  const top = sorted[0] || null;
  return { sorted, total, top, second: sorted[1] || null, agreement: top && total ? top.samples / total : 0 };
}

/** Does a candidate meet an evidence bar? bar = { minSamples, minProjects, minAgreement }. */
export function meetsBar({ samples, projects, agreement }, bar) {
  return samples >= bar.minSamples && projects >= bar.minProjects && agreement >= bar.minAgreement - 1e-9;
}

export const proposalBar = (t) => ({ minSamples: t.LEARN_MIN_SAMPLES, minProjects: t.LEARN_MIN_PROJECTS, minAgreement: t.LEARN_MIN_AGREEMENT });
export const predictionBar = (t) => ({ minSamples: t.LEARN_PREDICT_MIN_SAMPLES, minProjects: t.LEARN_PREDICT_MIN_PROJECTS, minAgreement: t.LEARN_PREDICT_MIN_AGREEMENT });

/** One percentage the way the admin reads it: 0.8333 -> "83%". */
export const pct = (x) => `${Math.round((x || 0) * 100)}%`;
